import { CloudStorageConfiguration } from '../types/storage';
import { addAuditLog } from './auditLog';
import { encryptData, decryptData } from './encryption';

const CLOUD_STORAGE_CONFIG_KEY = 'installtrack_cloud_storage';

/**
 * Get all cloud storage configurations
 */
export const getCloudStorageConfigs = (): CloudStorageConfiguration[] => {
  try {
    const encryptedData = localStorage.getItem(CLOUD_STORAGE_CONFIG_KEY);
    if (!encryptedData) return [];
    
    const decryptedData = decryptData(encryptedData);
    return JSON.parse(decryptedData, (key, value) => {
      if (key === 'createdAt' || key === 'updatedAt' || key === 'lastConnectionCheck') {
        return value ? new Date(value) : undefined;
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting cloud storage configs:', error);
    return [];
  }
};

/**
 * Save cloud storage configurations
 */
const saveCloudStorageConfigs = (configs: CloudStorageConfiguration[]): void => {
  try {
    const encryptedData = encryptData(JSON.stringify(configs));
    localStorage.setItem(CLOUD_STORAGE_CONFIG_KEY, encryptedData);
  } catch (error) {
    console.error('Error saving cloud storage configs:', error);
    throw error;
  }
};

/**
 * Save a cloud storage configuration
 */
export const saveCloudStorageConfig = async (config: CloudStorageConfiguration): Promise<void> => {
  try {
    const configs = getCloudStorageConfigs();
    const existingIndex = configs.findIndex(c => c.id === config.id);
    
    if (existingIndex >= 0) {
      configs[existingIndex] = config;
    } else {
      configs.push(config);
    }
    
    saveCloudStorageConfigs(configs);
    
    // Log the action
    addAuditLog(
      existingIndex >= 0 ? 'update' : 'create',
      'system',
      `Cloud storage configuration ${existingIndex >= 0 ? 'updated' : 'created'}: ${config.name}`,
      config.id
    );
  } catch (error) {
    console.error('Error saving cloud storage config:', error);
    throw error;
  }
};

/**
 * Delete a cloud storage configuration
 */
export const deleteCloudStorageConfig = async (id: string): Promise<void> => {
  try {
    const configs = getCloudStorageConfigs();
    const configToDelete = configs.find(c => c.id === id);
    
    if (!configToDelete) {
      throw new Error('Cloud storage configuration not found');
    }
    
    const filteredConfigs = configs.filter(c => c.id !== id);
    saveCloudStorageConfigs(filteredConfigs);
    
    // Log the action
    addAuditLog(
      'delete',
      'system',
      `Cloud storage configuration deleted: ${configToDelete.name}`,
      id
    );
  } catch (error) {
    console.error('Error deleting cloud storage config:', error);
    throw error;
  }
};

/**
 * Test connection to a cloud storage
 */
export const testCloudStorageConnection = async (config: CloudStorageConfiguration): Promise<boolean> => {
  try {
    // Try to create a test file
    const testContent = 'Connection Test';
    const testUrl = `${config.baseUrl}/${config.folderPath}/test.txt`;
    
    // For this demo, we'll just simulate the test
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In a real implementation, we would:
    // 1. Try to create a test file
    // 2. Verify we can read it
    // 3. Delete the test file
    
    // Update the configuration with successful connection status
    const configs = getCloudStorageConfigs();
    const configIndex = configs.findIndex(c => c.id === config.id);
    
    if (configIndex >= 0) {
      configs[configIndex] = {
        ...configs[configIndex],
        metadata: {
          ...configs[configIndex].metadata,
          connectionStatus: 'connected',
          lastConnectionCheck: new Date(),
          lastError: undefined
        }
      };
      saveCloudStorageConfigs(configs);
    }
    
    return true;
  } catch (error) {
    console.error('Error testing cloud storage connection:', error);
    
    // Update the configuration with error status
    const configs = getCloudStorageConfigs();
    const configIndex = configs.findIndex(c => c.id === config.id);
    
    if (configIndex >= 0) {
      configs[configIndex] = {
        ...configs[configIndex],
        metadata: {
          ...configs[configIndex].metadata,
          connectionStatus: 'error',
          lastConnectionCheck: new Date(),
          lastError: error instanceof Error ? error.message : 'Unknown error'
        }
      };
      saveCloudStorageConfigs(configs);
    }
    
    throw error;
  }
};

/**
 * Upload a file to cloud storage
 */
export const uploadToCloudStorage = async (
  file: File,
  config: CloudStorageConfiguration,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    // For demo purposes, we'll simulate the upload
    const totalSize = file.size;
    let uploadedSize = 0;
    const chunkSize = totalSize / 10;
    
    while (uploadedSize < totalSize) {
      await new Promise(resolve => setTimeout(resolve, 100));
      uploadedSize = Math.min(uploadedSize + chunkSize, totalSize);
      
      if (onProgress) {
        onProgress((uploadedSize / totalSize) * 100);
      }
    }
    
    // In a real implementation, we would:
    // 1. Upload the file to the configured cloud storage
    // 2. Return the public URL of the uploaded file
    
    const fileName = `${Date.now()}_${file.name}`;
    const fileUrl = `${config.baseUrl}/${config.folderPath}/${fileName}`;
    
    // Log the upload
    addAuditLog(
      'create',
      'file',
      `File uploaded to cloud storage: ${fileName}`,
      config.id
    );
    
    return fileUrl;
  } catch (error) {
    console.error('Error uploading to cloud storage:', error);
    throw error;
  }
};

/**
 * Get active cloud storage configuration
 */
export const getActiveCloudStorageConfig = (): CloudStorageConfiguration | null => {
  try {
    const configs = getCloudStorageConfigs();
    return configs.find(c => c.active) || null;
  } catch (error) {
    console.error('Error getting active cloud storage config:', error);
    return null;
  }
};